// Esclusioni testuali dell'Allegato II Reg. UE 1169/2011: derivati che, pur
// provenendo da una sostanza elencata, NON fanno scattare l'obbligo di
// dichiarazione dell'allergene. docs/etichettatura-alimentare-riferimento.md
// §4 richiama l'Allegato II per intero senza riportarne il testo puntuale
// delle esenzioni; il testo delle esclusioni sotto è quello dell'Allegato II
// consolidato (Reg. UE 1169/2011), citato anche nel piano W0.3 con gli stessi
// esempi (sciroppo di glucosio di grano/destrosio, maltodestrine di grano,
// olio e grasso di soia raffinati, lattitolo, gelatina di pesce come supporto
// vitaminico o chiarificante in birra/vino) — nessuna divergenza fra le due
// fonti su questo punto.
//
// Ogni pattern richiede che il testo espliciti anche l'USO esente (es. "come
// supporto", "chiarificante"), non la sola menzione della sostanza: la
// gelatina di pesce in un dolce non è esente solo perché si chiama così, lo è
// solo se dichiarata come supporto di vitamine/carotenoidi o come
// chiarificante di birra e vino (Allegato II punto 4).

import { type AllergenCategoryCode, getAllergenSubstanceById } from './allergen-substances';

/**
 * Fonte ufficiale dell'Allegato II Reg. UE 1169/2011 (versione consolidata
 * 01.04.2025), a cui ogni riferimento normativo mostrato all'utente per
 * un'esenzione o per la soglia dei solfiti deve collegarsi (regola di
 * progetto `.claude/rules/legal-references.md`).
 */
export const ALLERGEN_ANNEX_II_EUR_LEX_URL =
  'https://eur-lex.europa.eu/legal-content/IT/TXT/?uri=CELEX:02011R1169-20250401';

export interface AllergenExclusion {
  /** Chiave stabile dell'esclusione. */
  id: string;
  /** Categoria Allergen da cui la sostanza deriva. */
  categoryCode: AllergenCategoryCode;
  /** Descrizione del derivato esente, in italiano, come compare in etichetta. */
  description: string;
  /** Frammenti di testo (case/accent-insensitive) che identificano il derivato esente. */
  matchPatterns: readonly string[];
  /** Riferimento puntuale nell'Allegato II. */
  legalReference: string;
}

export const ALLERGEN_EXCLUSIONS: readonly AllergenExclusion[] = [
  // --- Allegato II punto 1: cereali contenenti glutine -----------------------
  {
    id: 'wheat-glucose-syrup',
    categoryCode: 'GLUTEN',
    description: 'Sciroppi di glucosio a base di grano, incluso il destrosio',
    matchPatterns: [
      'sciroppo di glucosio a base di grano',
      'sciroppo di glucosio di grano',
      'destrosio a base di grano',
    ],
    legalReference: 'Allegato II punto 1 lett. a Reg. UE 1169/2011',
  },
  {
    id: 'wheat-maltodextrin',
    categoryCode: 'GLUTEN',
    description: 'Maltodestrine a base di grano',
    matchPatterns: [
      'maltodestrine a base di grano',
      'maltodestrina a base di grano',
      'maltodestrine di grano',
    ],
    legalReference: 'Allegato II punto 1 lett. b Reg. UE 1169/2011',
  },
  {
    id: 'barley-glucose-syrup',
    categoryCode: 'GLUTEN',
    description: 'Sciroppi di glucosio a base di orzo',
    matchPatterns: ['sciroppo di glucosio a base di orzo', 'sciroppo di glucosio di orzo'],
    legalReference: 'Allegato II punto 1 lett. c Reg. UE 1169/2011',
  },
  {
    id: 'cereal-alcoholic-distillates',
    categoryCode: 'GLUTEN',
    description:
      "Cereali utilizzati per la fabbricazione di distillati alcolici, incluso l'alcol etilico di origine agricola",
    matchPatterns: [
      'distillato alcolico a base di grano',
      'distillato alcolico a base di cereali',
      'alcol etilico di origine agricola a base di grano',
      'alcol etilico di origine agricola a base di cereali',
    ],
    legalReference: 'Allegato II punto 1 lett. d Reg. UE 1169/2011',
  },

  // --- Allegato II punto 4: pesce ---------------------------------------------
  {
    id: 'fish-gelatin-vitamin-carrier',
    categoryCode: 'FISH',
    description:
      'Gelatina di pesce utilizzata come supporto per preparati di vitamine o carotenoidi',
    matchPatterns: [
      'gelatina di pesce come supporto',
      'gelatina di pesce utilizzata come supporto',
      'gelatina di pesce (supporto',
      'gelatina di pesce quale supporto',
    ],
    legalReference: 'Allegato II punto 4 lett. a Reg. UE 1169/2011',
  },
  {
    id: 'fish-gelatin-clarifying-agent',
    categoryCode: 'FISH',
    description: 'Gelatina o colla di pesce utilizzata come chiarificante nella birra e nel vino',
    matchPatterns: [
      'gelatina di pesce come chiarificante',
      'gelatina di pesce (chiarificante',
      'colla di pesce come chiarificante',
      'colla di pesce (chiarificante',
      'chiarificante per birra',
      'chiarificante per vino',
      'chiarificante nella birra',
      'chiarificante nel vino',
    ],
    legalReference: 'Allegato II punto 4 lett. b Reg. UE 1169/2011',
  },

  // --- Allegato II punto 6: soia -----------------------------------------------
  {
    id: 'soy-refined-oil-fat',
    categoryCode: 'SOY',
    description: 'Olio e grasso di soia raffinato',
    matchPatterns: [
      'olio di soia raffinato',
      'grasso di soia raffinato',
      'olio e grasso di soia raffinato',
      'olio e grasso di soia raffinati',
    ],
    legalReference: 'Allegato II punto 6 lett. a Reg. UE 1169/2011',
  },
  {
    id: 'soy-natural-tocopherols',
    categoryCode: 'SOY',
    description:
      'Tocoferoli misti naturali (E306), tocoferolo D-alfa naturale, tocoferolo acetato D-alfa naturale, tocoferolo succinato D-alfa naturale a base di soia',
    matchPatterns: [
      'tocoferoli misti naturali',
      'tocoferolo d-alfa naturale a base di soia',
      'e306 a base di soia',
    ],
    legalReference: 'Allegato II punto 6 lett. b Reg. UE 1169/2011',
  },
  {
    id: 'soy-phytosterols',
    categoryCode: 'SOY',
    description: 'Oli vegetali derivati da fitosteroli e fitosteroli esteri a base di soia',
    matchPatterns: ['fitosteroli a base di soia', 'fitosteroli esteri a base di soia'],
    legalReference: 'Allegato II punto 6 lett. c Reg. UE 1169/2011',
  },
  {
    id: 'soy-phytostanol-ester',
    categoryCode: 'SOY',
    description: 'Estere di stanolo vegetale prodotto da steroli di olio vegetale a base di soia',
    matchPatterns: ['estere di stanolo vegetale a base di soia', 'stanolo vegetale a base di soia'],
    legalReference: 'Allegato II punto 6 lett. d Reg. UE 1169/2011',
  },

  // --- Allegato II punto 7: latte ----------------------------------------------
  {
    id: 'milk-whey-distillates',
    categoryCode: 'MILK',
    description:
      "Siero di latte utilizzato per la fabbricazione di distillati alcolici, incluso l'alcol etilico di origine agricola",
    matchPatterns: [
      'siero di latte utilizzato per la fabbricazione di distillati',
      'siero di latte per distillati alcolici',
    ],
    legalReference: 'Allegato II punto 7 lett. a Reg. UE 1169/2011',
  },
  {
    id: 'lactitol',
    categoryCode: 'MILK',
    description: 'Lattitolo',
    matchPatterns: ['lattitolo'],
    legalReference: 'Allegato II punto 7 lett. b Reg. UE 1169/2011',
  },

  // --- Allegato II punto 8: frutta a guscio ------------------------------------
  {
    id: 'tree-nuts-alcoholic-distillates',
    categoryCode: 'NUTS',
    description:
      "Frutta a guscio utilizzata per la fabbricazione di distillati alcolici, incluso l'alcol etilico di origine agricola",
    matchPatterns: [
      'distillato alcolico a base di frutta a guscio',
      'alcol etilico di origine agricola a base di frutta a guscio',
    ],
    legalReference: 'Allegato II punto 8 Reg. UE 1169/2011',
  },

  // --- Allegato II punto 10: senape, come modificato dal Reg. delegato UE 2024/2512 ---
  // Testo consolidato 02011R1169 — IT — 01.04.2025 — 004.001, punto 10: alinea
  // unico senza lettera, senza la specificazione "da semi di senape" (il piano
  // A §4.5 corregge la voce precedente, divergente dalla fonte).
  //
  // Difetto C1 (revisione avversariale 2026-08-19): i pattern includevano i
  // soli numeri E ("e471", "e 470a", ...), che compaiono in QUALUNQUE testo
  // che nomini quell'emulsionante generico — la stragrande maggioranza dei
  // quali non ha nulla a che vedere con l'acido beenico purificato. L'esenzione
  // riguarda l'acido beenico qualificato (purezza minima, doppia distillazione)
  // usato nella fabbricazione di quegli emulsionanti, non l'emulsionante
  // comunque ottenuto: un margarina con "emulsionante E471" generico deve
  // continuare a far scattare la dichiarazione di senape. Il pattern resta
  // quindi ancorato alla dicitura dell'acido beenico stesso.
  {
    id: 'mustard-refined-emulsifiers',
    categoryCode: 'MUSTARD',
    description:
      'Acido beenico con una purezza minima dell’85% e ottenuto dopo due fasi di distillazione, utilizzato nella fabbricazione degli emulsionanti E 470a, E 471 ed E 477',
    matchPatterns: ['acido beenico'],
    legalReference:
      'Allegato II punto 10 Reg. UE 1169/2011, come modificato dal Reg. delegato UE 2024/2512',
  },
];

const COMBINING_DIACRITICAL_MARKS_PATTERN = /[̀-ͯ]/g;

function normalizeForMatching(text: string): string {
  return text.normalize('NFD').replace(COMBINING_DIACRITICAL_MARKS_PATTERN, '').toLowerCase();
}

/** Trova la prima esclusione dell'Allegato II il cui pattern compare nel testo. */
export function findMatchingAllergenExclusion(
  text: string,
  categoryCode?: AllergenCategoryCode,
): AllergenExclusion | undefined {
  const normalizedText = normalizeForMatching(text);
  return ALLERGEN_EXCLUSIONS.find((exclusion) => {
    if (categoryCode && exclusion.categoryCode !== categoryCode) {
      return false;
    }
    return exclusion.matchPatterns.some((pattern) =>
      normalizedText.includes(normalizeForMatching(pattern)),
    );
  });
}

/** true se il testo descrive un derivato esente dalla dichiarazione dell'allergene. */
export function isExcludedFromAllergenDeclaration(
  text: string,
  categoryCode?: AllergenCategoryCode,
): boolean {
  return findMatchingAllergenExclusion(text, categoryCode) !== undefined;
}

export interface AllergenExclusionMatch {
  exclusionId: string;
  legalReference: string;
  description: string;
}

/**
 * Data una chiave di sostanza del catalogo (`AllergenSubstance.id`) e il testo
 * che ha generato la dichiarazione, restituisce l'esenzione dell'Allegato II
 * applicabile, o `undefined`. Risolve da sé la categoria della sostanza: i
 * chiamanti non devono mai duplicare la mappa sostanza -> categoria. Una
 * chiave di sostanza sconosciuta non esenta mai.
 */
export function findExclusionForSubstance(
  substanceCatalogKey: string,
  sourceText: string,
): AllergenExclusionMatch | undefined {
  const substance = getAllergenSubstanceById(substanceCatalogKey);
  if (!substance) {
    return undefined;
  }
  const exclusion = findMatchingAllergenExclusion(sourceText, substance.categoryCode);
  if (!exclusion) {
    return undefined;
  }
  return {
    exclusionId: exclusion.id,
    legalReference: exclusion.legalReference,
    description: exclusion.description,
  };
}
